import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Layout from "./Layout/Layout";
import PropTypes from 'prop-types';
import useAuth from '../hooks/useAuth';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { getCurrentUser } from '../slice/AuthSlice';
import Loader from './Loader/Loader';
import { sessionEndedReason } from '../utils/idleSession';
import { consumeReturnTo, rememberReturnTo } from '../utils/returnTo';

// Salesforce sign-in always lands on /home. The first time it does in this
// page load, carry on to the page the sign-in interrupted, if any.
let returnToChecked = false;

const ProtectedRoute = ({ element, isAuthenticated, path, isAdminOnly, noLayout }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user } = useAuth();
  const authStatus = useSelector((state) => state.auth.status);
  const endedReason = sessionEndedReason();
  const signedOut = Boolean(endedReason);
  
  // Dispatch getCurrentUser if not already loading/loaded
  useEffect(() => {
    if (signedOut) return;
    if ((authStatus === '' || authStatus === 'idle') && !user) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, authStatus, user, signedOut]);

  useEffect(() => {
    if (signedOut || returnToChecked) return;
    returnToChecked = true;
    if (location.pathname !== '/home') return;
    const returnTo = consumeReturnTo();
    if (returnTo) navigate(returnTo, { replace: true });
  }, [signedOut, location.pathname, navigate]);

  // Once WHG Docs has signed the person out, no page shows anything but
  // the sign-in page — not even one reached with the back button.
  if (signedOut) {
    if (endedReason !== 'signed_out') {
      rememberReturnTo(`${location.pathname}${location.search}`);
    }
    return <Navigate to="/" replace />;
  }
  
  // Wait for auth to load before checking admin status
  // This prevents redirecting admin users on page refresh
  if (isAdminOnly) {
    // If auth is still loading or user is not yet loaded, show loader
    if (authStatus === 'loading' || (!user && authStatus !== 'failed')) {
      return <Loader />;
    }
    // Only redirect if user is loaded and is not admin
    if (!isAdmin) {
      return <Navigate to="/matter" />;
    }
  }
  
  if (!isAuthenticated) return <Navigate to={path} />;
  return noLayout ? element : <Layout>{element}</Layout>;
};

export default ProtectedRoute;
ProtectedRoute.propTypes = {
  element: PropTypes.node.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  path: PropTypes.string.isRequired,
  isAdminOnly: PropTypes.bool,
  noLayout: PropTypes.bool,
};
